import { Component, OnInit, OnDestroy, inject, signal, computed, WritableSignal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder,  Validators } from '@angular/forms';
import { UsuarioService } from 'src/app/services/usuario.service';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';
import * as bootstrap from 'bootstrap';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';
import { firstValueFrom, Observable } from 'rxjs';

@Component({
    selector: 'app-my-account-identidad-prestador',
    templateUrl: './my-account-identidad-prestador.component.html',
    styleUrls: ['./my-account-identidad-prestador.component.css'],
    standalone: false
})
export class MyAccountIdentidadPrestadorComponent  implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _usuarioService = inject(UsuarioService);
  private _fb = inject(FormBuilder);
  private _location = inject(Location);
  private _loadingService = inject(LoadingService);
  private _authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  // ------- estado -------
  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  // máscara
  maskDocumento = signal<string>('00-00000000-0');

  // combos
  tiposDocs = computed<TipoDocumento[]>(() => {
    const base = this._usuarioService.tiposDocumentos() ?? [];
    return [{ id: 0, x_descripcion: 'Seleccione', b_activo: true }, ...base];
  });

  // archivos + previews
  x_img_avatar?: File;
  x_img_dni_frente?: File;
  x_img_dni_dorso?: File;
  x_img_matricula_frente?: File;
  x_img_matricula_dorso?: File;

  preview_img_avatar = signal<string>('');
  preview_img_avatar_large = signal<string>('');
  preview_img_dni_frente = signal<string>('');
  preview_img_dni_dorso = signal<string>('');
  preview_img_matricula_frente = signal<string>('');
  preview_img_matricula_dorso = signal<string>('');
  porcentaje_comision_libresalud = signal<number>(0);

  private modalInstance?: any;

  // Form
  readonly form = this._fb.nonNullable.group({
    nombre: this._fb.nonNullable.control('', Validators.required),
    apellido: this._fb.nonNullable.control('', Validators.required),
    telefono: this._fb.nonNullable.control('', [Validators.required, Validators.pattern(/^[0-9]*$/)]),
    tipo_documento: this._fb.nonNullable.control(0, Validators.required),
    nro_documento: this._fb.nonNullable.control('', [Validators.required, Validators.maxLength(13)]),
    nro_matricula: this._fb.nonNullable.control('', [Validators.required, Validators.pattern(/^[0-9]*$/)]),
    reprogramacion_turno: this._fb.nonNullable.control(true, Validators.required),
  });

  // -------- Solo números en teléfono / matrícula --------
  soloNumeros(controlName: 'telefono' | 'nro_matricula', event: Event) {
    const input = event.target as HTMLInputElement;
    const filtrado = input.value.replace(/\D/g, '');
    if (filtrado !== input.value) {
      this.form.get(controlName)?.setValue(filtrado);
    }
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // cerrar modal al volver atrás
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl && this.modalInstance) this.modalInstance.hide();
    });

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch {
        this._router.navigate(['/sign-in']);
        return;
      }

      const prestador = this._usuarioService.prestador?.() as any;
      const usuario = this._usuarioService.usuario?.() as any;

      if (!prestador?.id || !usuario?.id) {
        this.error.set('No se pudo cargar tu cuenta. Volvé a intentar.');
        this._router.navigate(['/sign-in']);
        return;
      }

      // patch
      // Importante: fijamos tipo_documento y la máscara ANTES de cargar
      // nro_documento. El cambio de [mask] recién llega al input en el
      // próximo ciclo de detección de cambios de Angular (no es sincrónico
      // con el .set() del signal) — por eso el patchValue de nro_documento
      // va en un setTimeout: si se hace en el mismo tick, ngx-mask todavía
      // tiene puesta la máscara vieja de DNI (8 dígitos) y trunca un
      // CUIT/CUIL (11 dígitos), aunque en la base el dato esté completo.
      this.form.patchValue({
        nombre: prestador.x_nombre?.trim() ?? '',
        apellido: prestador.x_apellido?.trim() ?? '',
        telefono: prestador.x_telefono?.trim() ?? '',
        tipo_documento: usuario.id_tipo_documento ?? 0,
      });
      this.actualizarMascara();
      setTimeout(() => {
        this.form.patchValue({
          nro_documento: usuario.x_nro_documento?.trim() ?? '',
          nro_matricula: prestador.x_nro_matricula?.trim() ?? '',
          reprogramacion_turno: prestador.b_reprogramacion_turno ?? true,
        });
      });

      // porcentaje comisión (solo lectura)
      this.porcentaje_comision_libresalud.set(usuario.n_porcentaje_comision ?? 0);

      // previews
      if (usuario.x_img_avatar) {
        this.preview_img_avatar.set(`${API_URI}uploads/images/small-${usuario.x_img_avatar}`);
        this.preview_img_avatar_large.set(`${API_URI}uploads/images/large-${usuario.x_img_avatar}`);
      }
      if (prestador.x_img_dni_frente) this.preview_img_dni_frente.set(`${API_URI}uploads/images/${prestador.x_img_dni_frente}`);
      if (prestador.x_img_dni_dorso) this.preview_img_dni_dorso.set(`${API_URI}uploads/images/${prestador.x_img_dni_dorso}`);
      if (prestador.x_img_matricula_frente) this.preview_img_matricula_frente.set(`${API_URI}uploads/images/${prestador.x_img_matricula_frente}`);
      if (prestador.x_img_matricula_dorso) this.preview_img_matricula_dorso.set(`${API_URI}uploads/images/${prestador.x_img_matricula_dorso}`);
    });

    this.form.controls.tipo_documento.valueChanges.subscribe(() => this.actualizarMascara());
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
    if (this.modalConfirmarEliminarInstance) this.modalConfirmarEliminarInstance.hide();
  }

  back() {
    this._location.back();
  }

  // -------- Modal foto perfil --------
  openModal() {
    const modalEl = document.getElementById('modalPreviewImg');
    if (modalEl) {
      this.modalInstance = new (bootstrap as any).Modal(modalEl);
      this.modalInstance.show();
    }
  }

  // -------- Máscara --------
  actualizarMascara() {
    const tipo = this.form.getRawValue().tipo_documento;
    if (tipo === 1 || tipo === 2) {
      this.maskDocumento.set('00-00000000-0'); // CUIT/CUIL
    } else {
      this.maskDocumento.set('00000000'); // DNI
    }
  }

   // -------- Guardar --------
  async saveUsuario(): Promise<void> {
    this.error.set('');
    if (this.loading() || this.form.pending) return;

    const v = this.form.getRawValue();

    // Validaciones a mano como el legacy
    if (!v.apellido) { this.error.set('Debe Completar el Apellido'); return; }
    if (!v.nombre) { this.error.set('Debe Completar el Nombre'); return; }
    if (!v.telefono) { this.error.set('Debe Completar el Teléfono'); return; }
    if (!/^[0-9]*$/.test(v.telefono)) { this.error.set('El Celular / WhatsApp debe contener solo números'); return; }
    if (!v.nro_documento) { this.error.set('Debe Completar el Nro. de Documento'); return; }
    if ((v.nro_documento ?? '').length > 13) { this.error.set('El número de documento no puede tener más de 13 caracteres'); return; }
    if (!v.tipo_documento || v.tipo_documento === 0) { this.error.set('Debe Seleccionar el Tipo de Documento'); return; }
    if (!v.nro_matricula) { this.error.set('Debe Completar el Nro. de Matrícula'); return; }
    if (!/^[0-9]*$/.test(v.nro_matricula)) { this.error.set('El Nro. de Matrícula debe contener solo números'); return; }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    await this._loadingService.run(async () => {
      try {
        this.form.disable({ emitEvent: false });

        // 1) actualizar prestador en memoria (clonado)
        const p = { ...(this._usuarioService.prestador() as any) };
        p.x_apellido = v.apellido;
        p.x_nombre = v.nombre;
        p.x_telefono = String(v.telefono ?? '').trim();
        p.x_nro_matricula = String(v.nro_matricula ?? '').trim();
        p.b_reprogramacion_turno = v.reprogramacion_turno;
        this._usuarioService.prestador.set(p);

        const usu = { ...(this._usuarioService.usuario() ?? {}) } as any;
        usu.id_tipo_documento = v.tipo_documento!;
        usu.x_nro_documento = v.nro_documento!;
        this._usuarioService.usuario.set(usu);

        const userId = this._usuarioService.usuario().id!;
        const email = this._usuarioService.usuario().x_email!;

        // 2) subir imágenes (solo las que existan)
        // Avatar
        if (this.x_img_avatar) {
          const res: any = await firstValueFrom(this._usuarioService.updateUsuarioImgAvatar(this.x_img_avatar, email));
          const u = { ...this._usuarioService.usuario() };
          u.x_img_avatar = res?.imagen ?? u.x_img_avatar;
          if ((this._usuarioService as any).usuario?.set) (this._usuarioService as any).usuario.set(u);
        }

        // DNI frente/dorso, matrícula frente/dorso
        if (this.x_img_dni_frente) {
          const res: any = await firstValueFrom(this._usuarioService.updatePrestadorImgDniFrente(this.x_img_dni_frente, userId));
          const p2 = { ...(this._usuarioService.prestador() as any) };
          p2.x_img_dni_frente = res?.imagen ?? p2.x_img_dni_frente;
          if ((this._usuarioService as any).prestador?.set) (this._usuarioService as any).prestador.set(p2);
        }

        if (this.x_img_dni_dorso) {
          const res: any = await firstValueFrom(this._usuarioService.updatePrestadorImgDniDorso(this.x_img_dni_dorso, userId));
          const p2 = { ...(this._usuarioService.prestador() as any) };
          p2.x_img_dni_dorso = res?.imagen ?? p2.x_img_dni_dorso;
          if ((this._usuarioService as any).prestador?.set) (this._usuarioService as any).prestador.set(p2);
        }

        if (this.x_img_matricula_frente) {
          const res: any = await firstValueFrom(this._usuarioService.updatePrestadorImgMatriculaFrente(this.x_img_matricula_frente, userId));
          const p2 = { ...(this._usuarioService.prestador() as any) };
          p2.x_img_matricula_frente = res?.imagen ?? p2.x_img_matricula_frente;
          if ((this._usuarioService as any).prestador?.set) (this._usuarioService as any).prestador.set(p2);
        }

        if (this.x_img_matricula_dorso) {
          const res: any = await firstValueFrom(this._usuarioService.updatePrestadorImgMatriculaDorso(this.x_img_matricula_dorso, userId));
          const p2 = { ...(this._usuarioService.prestador() as any) };
          p2.x_img_matricula_dorso = res?.imagen ?? p2.x_img_matricula_dorso;
          if ((this._usuarioService as any).prestador?.set) (this._usuarioService as any).prestador.set(p2);
        }

        // 3) update prestador y usuario
        await firstValueFrom(this._usuarioService.updatePrestador(this._usuarioService.prestador()));
        await firstValueFrom(this._usuarioService.updateUsuario(this._usuarioService.usuario()));

        // 4) Refrescamos si es socio o no
        await this._usuarioService.refreshAsociadoCardio();

        // 5) navegar
        await this._router.navigate(['/my-account']);
      } catch (e) {
        console.error(e);
        this.error.set('Error al actualizar el prestador');
      } finally {
        this.form.enable({ emitEvent: false });
      }
    });
  }

  // ------------------ Helpers imagen ------------------

  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      resultType: CameraResultType.Uri,
      quality: 90,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  // ------------------ Capturas ------------------

  async capturarFileImgAvatar(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('avatar.jpg');
      this.x_img_avatar = file;
      this.preview_img_avatar.set(dataUrl);
      this.preview_img_avatar_large.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar avatar:', e);
    }
  }

  async capturarFileImgDniFrente(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-frente.jpg');
      this.x_img_dni_frente = file;
      this.preview_img_dni_frente.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar DNI frente:', e);
    }
  }

  async capturarFileImgDniDorso(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-dorso.jpg');
      this.x_img_dni_dorso = file;
      this.preview_img_dni_dorso.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar DNI dorso:', e);
    }
  }

  async capturarFileImgMatriculaFrente(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('matricula-frente.jpg');
      this.x_img_matricula_frente = file;
      this.preview_img_matricula_frente.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar matrícula frente:', e);
    }
  }

  async capturarFileImgMatriculaDorso(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('matricula-dorso.jpg');
      this.x_img_matricula_dorso = file;
      this.preview_img_matricula_dorso.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar matrícula dorso:', e);
    }
  }

  // ------------------ Eliminar imágenes ------------------

  nombreImagenAEliminar = signal<string>('');
  private modalConfirmarEliminarInstance?: any;
  private accionEliminarPendiente: (() => Promise<void>) | null = null;

  private abrirConfirmarEliminarImagen(nombre: string, accion: () => Promise<void>): void {
    this.nombreImagenAEliminar.set(nombre);
    this.accionEliminarPendiente = accion;
    const modalEl = document.getElementById('modalConfirmarEliminarImagen');
    if (modalEl) {
      this.modalConfirmarEliminarInstance = new (bootstrap as any).Modal(modalEl);
      this.modalConfirmarEliminarInstance.show();
    }
  }

  async confirmarEliminarImagen(): Promise<void> {
    const accion = this.accionEliminarPendiente;
    this.accionEliminarPendiente = null;
    this.modalConfirmarEliminarInstance?.hide();
    if (accion) await accion();
  }

  private async ejecutarEliminarImagen(
    borrar: (id: string) => Observable<any>,
    previewSignal: WritableSignal<string>,
    campo: 'x_img_dni_frente' | 'x_img_dni_dorso' | 'x_img_matricula_frente' | 'x_img_matricula_dorso',
    limpiarArchivo: () => void
  ): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        const userId = this._usuarioService.usuario().id!;
        await firstValueFrom(borrar(userId));
        const p = { ...(this._usuarioService.prestador() as any) };
        p[campo] = null;
        this._usuarioService.prestador.set(p);
        previewSignal.set('');
        limpiarArchivo();
      } catch (e) {
        console.error('Error al eliminar la imagen:', e);
        this.error.set('No se pudo eliminar la imagen. Intentá nuevamente.');
      }
    });
  }

  eliminarImgDniFrente(): void {
    this.abrirConfirmarEliminarImagen('el DNI frente', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deletePrestadorImgDniFrente(id),
        this.preview_img_dni_frente,
        'x_img_dni_frente',
        () => this.x_img_dni_frente = undefined
      )
    );
  }

  eliminarImgDniDorso(): void {
    this.abrirConfirmarEliminarImagen('el DNI dorso', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deletePrestadorImgDniDorso(id),
        this.preview_img_dni_dorso,
        'x_img_dni_dorso',
        () => this.x_img_dni_dorso = undefined
      )
    );
  }

  eliminarImgMatriculaFrente(): void {
    this.abrirConfirmarEliminarImagen('la Matrícula frente', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deletePrestadorImgMatriculaFrente(id),
        this.preview_img_matricula_frente,
        'x_img_matricula_frente',
        () => this.x_img_matricula_frente = undefined
      )
    );
  }

  eliminarImgMatriculaDorso(): void {
    this.abrirConfirmarEliminarImagen('la Matrícula dorso', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deletePrestadorImgMatriculaDorso(id),
        this.preview_img_matricula_dorso,
        'x_img_matricula_dorso',
        () => this.x_img_matricula_dorso = undefined
      )
    );
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })
}
